/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _debounce from 'lodash/debounce';
import 'cordova-plugin-purchase';
import _forEach from 'lodash/forEach';
import reportError from './error-handler/reportError';
import InAppPurchasesConst from '../constants/inapppurchases.constant';
import { sendSlackNotification } from './sendSlackNotifications';
import { getIsInAppPurchaseActive } from './subscription/getIsInAppPurchaseActive';
import { setIsInAppPurchaseActive } from './subscription/setIsInAppPurchaseActive';
import { getUserDetails } from './user/getUserDetails';
import { setIapProductList } from './iap/setIapProductList';
import { alertCanNotConnectToIap, alertPurchaseFailed } from './iap/alertsForPurchase';
import { setT1T2Cognito } from './aws/user/setAttributes/setT1T2Cognito';
import { getAppleIapSubscriptionExpiryDate } from './iap/getAppleIapSubscriptionExpiryDate';

const inAppPurchaseService = () => {
  const contents = {};
  let lockStoreErrorFunction = false;

  function registerProductStore(productId) {
    window.CdvPurchase.store.register({
      id: productId,
      alias: productId,
      type: window.CdvPurchase.store.NON_RENEWING_SUBSCRIPTION,
      platform: window.CdvPurchase.Platform.APPLE_APPSTORE,
    });
  }
  contents.bindStoreFinally = () => {
    if (window.CdvPurchase.store) {
      // When store is ready, activate the "refresh" button;
      window.CdvPurchase.store.ready(() => {
        console.log('store ready!');
        contents.getAllProducts();
      });
    }
  };
  contents.getProduct = (productId) => {
    if (window.CdvPurchase.store) {
      return window.CdvPurchase.store.get(productId);
    }
    return null;
  };
  contents.getAllProducts = () => {
    if (window.CdvPurchase.store) {
      const products = InAppPurchasesConst.products.map((product) => {
        const productDetails = contents.getProduct(product);
        if (!productDetails || !productDetails.title || !productDetails.pricing.price) {
          const errorLog = {
            message: 'Unable to load valid product for apple IAP',
            code: 'Invalid apple IAP product',
            name: 'Apple IAP products issue',
            productId: product,
            productDataLoaded: JSON.stringify(productDetails),
          };
          sendSlackNotification(errorLog);
          reportError(errorLog);
        }
        return {
          ...productDetails,
          price: productDetails.pricing.price,
        };
      });
      setIapProductList(products.filter((p) => p));
      console.log(products);
    }
    return null;
  };
  contents.logIAPDone = _debounce((status = 'done', exception = undefined) => {
    const userDetails = getUserDetails();
    const sentryLogData = {
      message: `User ${userDetails.email} did Apple IAP: ${status}`,
      email: userDetails.email,
      boughtOn: new Date().toString(),
      userDetails,
      exception,
    };
    if (userDetails && userDetails.given_name) {
      sentryLogData.name = `${userDetails.given_name} ${userDetails.family_name}`;
    }
    const extraTag = {
      username: userDetails.email,
    };
    if (sentryLogData.name) {
      extraTag.nameOfUser = sentryLogData.name;
    }
    reportError(sentryLogData, extraTag);
    sendSlackNotification({
      sentryLogData,
      extraTag,
    });
  }, 1000);
  contents.buySubscription = (productId, expiryDate) => new Promise((resolve) => {
    if (window.CdvPurchase.store) {
      // Called when the user has cancelled purchasing the product, after it has
      // been initiated.
      window.CdvPurchase.store.when(productId).unverified(() => {
        // Write a function that marks this product ID as not being purchased
        // my_app_utils.setIsProductPurchaseInitiated("my_product_id", false);
        console.log('Purchase cancelled by user.');
        resolve({
          status: false,
          wasCancelledByUser: true,
        });
      });
      // Called when the product purchase is finished. This gets called every time
      // the app starts after the product has been purchased, so we use a helper
      // function to determine if we actually need to purchase the non-renewing
      // subscription on our own server.
      window.CdvPurchase.store.when(productId).approved(async (iap_tx_details) => {
        console.log('@@', iap_tx_details.purchaseDate);
        console.log('$$', iap_tx_details);
        if (iap_tx_details.purchaseDate) {
          contents.logIAPDone('Approved at APPLE SIDE', iap_tx_details);
          const expiryDateOfPurchase = getAppleIapSubscriptionExpiryDate(
            productId,
            iap_tx_details.purchaseDate,
          );
          const { error } = await setT1T2Cognito(productId, expiryDateOfPurchase, iap_tx_details);
          if (!error) {
          // Purchase has been executed successfully.
          // Must call finish to charge the user and allow the purchase to be
          // made again.
            contents.logIAPDone();
            console.log('Purchase successful');
            iap_tx_details.finish();
            resolve({ status: true, expiryDateOfPurchase });
          } else {
          // Failed to deliver the subscription (link issue with server?).
          // Report to user, do not finish the transaction (so it pops up
          // next time the user starts the app).
            alertPurchaseFailed();
            resolve({
              status: false,
            });
          }
        }
      });
      // Errors communicating with the iTunes server happen quite often,
      // so it's highly recommended you implement some feedback to the user.
      window.CdvPurchase.store.error((e) => {
        console.log(`APPLE IAP Store ERROR ${e.code}: ${e.message}`, e);
        if (lockStoreErrorFunction === false) {
          lockStoreErrorFunction = true;
          console.log(`storekit ERROR ${e.code}: ${e.message}`);
          alertCanNotConnectToIap();
          window.setTimeout(() => {
            lockStoreErrorFunction = false;
          }, 1000);
          contents.logIAPDone('Failed', e);
        }
        resolve({ status: false });
      });
      const productDetails = contents.getProduct(productId);
      productDetails.getOffer().order();
    } else {
      alertCanNotConnectToIap();
      resolve({ status: false });
    }
  });
  contents.load = () => {
    if (!window.CdvPurchase.store) {
      console.log('Store not available.');
      return;
    }
    setIsInAppPurchaseActive().then(() => {
      if (!getIsInAppPurchaseActive()) {
        //console.log('IAP not active.');
        return;
      }
      if (window.CdvPurchase.store) {
        // Enable maximum logging level
        window.CdvPurchase.store.verbosity = window.CdvPurchase.store.ERROR;
        // First register all products
        _forEach(InAppPurchasesConst.products, (product) => {
          registerProductStore(product);
        });
        contents.bindStoreFinally();
        window.CdvPurchase.store.initialize();
      }
    });
  };
  return contents;
};
export default inAppPurchaseService();
